// The company surface as content, not chrome. Legal and trust pages are
// written at product quality and in Beat Draft's voice (docs/BRAND.md, COPY.md):
// plain, warm, honest, no em dashes, no legalese theatre. One registry, one
// renderer, so every page stays consistent and easy to keep current.

export interface PageSection {
  h: string;
  body: string[]; // paragraphs
  list?: string[]; // optional bullet list after the paragraphs
}

export interface CompanyPage {
  slug: string;
  kind: "legal" | "company";
  title: string;
  updated: string; // ISO date
  intro: string;
  sections: PageSection[];
}

const UPDATED = "2026-07-01";

export const COMPANY_PAGES: CompanyPage[] = [
  {
    slug: "about",
    kind: "company",
    title: "About Beat Draft",
    updated: UPDATED,
    intro:
      "We believe music should never be finished. It should evolve. Beat Draft is the place where music lives: a creative universe where every idea, song, mistake, remix and performance stays alive, keeps a biography, and can always grow into something new.",
    sections: [
      {
        h: "Why we exist",
        body: [
          "Most tools treat a song as a file: you open it, you edit it, you export it, and it ends. We think that is backwards. A song is a living thing. It has a beginning, a story, collaborators, performances, and a future.",
          "So we built the opposite of a file. Everything you make here is an object with identity, history and provenance. Publishing does not freeze a song. It begins its life in the world.",
        ],
      },
      {
        h: "Who it is for",
        body: [
          "Everyone, from someone who has never made a sound before to a producer with a wall of platinum records. The interface adapts to you. Rhodes, our producer, explains every move and grows to understand your taste over years.",
        ],
      },
      {
        h: "The five ideas we exist to introduce",
        body: ["These are the beliefs every design decision defends."],
        list: [
          "The Living Song: publishing begins a song's life instead of ending it.",
          "Musical provenance: a public record of where every idea came from and everywhere it went.",
          "Replayable creativity: the making of music as a scrubbable, forkable medium.",
          "Emotion first: feelings, not file formats, as the native vocabulary of making music.",
          "A producer that knows you: taste that compounds over years, not a stateless assistant.",
        ],
      },
    ],
  },
  {
    slug: "trust",
    kind: "company",
    title: "Trust Center",
    updated: UPDATED,
    intro:
      "Your creative life is the most personal thing you can keep somewhere. Here is how we protect it, what we promise, and how to reach us when something is wrong.",
    sections: [
      {
        h: "Your work is yours",
        body: [
          "You own what you make. We claim no ownership of your music. Publishing a Session grants other creators the rights you choose through its license, and nothing more.",
          "Your universe works offline and lives on your device first. When you sign in, it mirrors to the cloud so you can reach it anywhere and never lose it.",
        ],
      },
      {
        h: "Security",
        body: [
          "Passwords are hashed with PBKDF2 and never stored in the clear. Sessions use signed, HTTP-only cookies. Traffic is encrypted in transit.",
          "Read the full picture on the Security page, and report anything concerning to security@beatdraft.com.",
        ],
      },
      {
        h: "Privacy and analytics",
        body: [
          "Nothing leaves your device for analytics until you say yes. Reflections, your creative story, is computed from your own graph, never from any tracking layer.",
          "You can change your mind at any time from Cookie preferences.",
        ],
      },
    ],
  },
  {
    slug: "security",
    kind: "company",
    title: "Security",
    updated: UPDATED,
    intro: "How we protect accounts, work and data, and how to tell us if you find a problem.",
    sections: [
      {
        h: "Accounts",
        body: [
          "Passwords are hashed with PBKDF2-SHA-256 at 210,000 iterations using the Web Crypto API. Session tokens are random, hashed at rest, and delivered in HTTP-only cookies with a sliding expiry.",
          "Magic-link sign-in is supported. Where email delivery is not configured, links are shown only to the requesting device.",
        ],
      },
      {
        h: "Data",
        body: [
          "Your universe is stored locally first and mirrored to Cloudflare D1 when you are signed in. Realtime jams run through isolated Durable Object rooms with per-message permission checks.",
        ],
      },
      {
        h: "Reporting a vulnerability",
        body: [
          "We welcome good-faith security research. Email security@beatdraft.com with details and steps to reproduce. We will acknowledge quickly, keep you updated, and credit you if you would like.",
          "Please do not access other people's data, degrade the service, or run automated scans that affect availability.",
        ],
      },
    ],
  },
  {
    slug: "contact",
    kind: "company",
    title: "Contact",
    updated: UPDATED,
    intro: "Real people read these. We try to reply within a few days.",
    sections: [
      {
        h: "How to reach us",
        body: ["Pick the address that fits and we will route it from there."],
        list: [
          "General and support: hello@beatdraft.com",
          "Security reports: security@beatdraft.com",
          "Copyright and takedowns: dmca@beatdraft.com",
          "Press and partnerships: press@beatdraft.com",
        ],
      },
    ],
  },
  {
    slug: "careers",
    kind: "company",
    title: "Careers",
    updated: UPDATED,
    intro: "We are building the home of living music. We hire people who care about craft, creators, and getting the details right.",
    sections: [
      {
        h: "How we work",
        body: [
          "Small team, high trust, docs before code. We believe beauty improves usability, latency destroys creativity, and nothing generic ships.",
        ],
      },
      {
        h: "Open roles",
        body: [
          "We do not have public openings right now. If Beat Draft is the kind of thing you would give years to, tell us why at hello@beatdraft.com and we will keep it on file.",
        ],
      },
    ],
  },
  {
    slug: "press",
    kind: "company",
    title: "Press Kit",
    updated: UPDATED,
    intro: "The short version, for anyone writing about Beat Draft.",
    sections: [
      {
        h: "One line",
        body: ["Beat Draft is the home of living music: a creative universe where songs are born, evolve, and never become just a file."],
      },
      {
        h: "One paragraph",
        body: [
          "Beat Draft is a browser-based creative universe for making music. You type a feeling and hear a living, editable song in seconds. Every idea keeps a history you can scrub and fork, every song gets a public biography when you release it, and a producer named Rhodes grows to understand your taste over years. It works offline, welcomes complete beginners and professionals alike, and treats music as a living medium rather than a collection of files.",
        ],
      },
      {
        h: "Facts",
        body: ["Use these freely."],
        list: [
          "Founded to make music that never has to be finished.",
          "Works offline and anonymously; sign in to sync and share.",
          "Free to create; creator marketplace and payments are opt-in.",
          "Reach press@beatdraft.com for assets and interviews.",
        ],
      },
    ],
  },
  {
    slug: "brand",
    kind: "company",
    title: "Brand Guidelines",
    updated: UPDATED,
    intro: "How to talk about and show Beat Draft so it always feels like itself.",
    sections: [
      {
        h: "Name",
        body: ["Always two words, both capitalized: Beat Draft. Never Beatdraft, BeatDraft or beatdraft in prose. The domain beatdraft.com is fine."],
      },
      {
        h: "Voice",
        body: [
          "Warm, plain, confident, never hype. We speak like a great producer in the room: encouraging, specific, honest.",
          "We never call ourselves a browser DAW, a music editor, or an AI tool. The words are creative universe, living music, and the home of music.",
        ],
      },
      {
        h: "Writing",
        body: ["We do not use em dashes anywhere in our writing. We prefer short sentences, commas, and full stops."],
      },
    ],
  },
  {
    slug: "privacy",
    kind: "legal",
    title: "Privacy Policy",
    updated: UPDATED,
    intro: "What we collect, why, and the control you have. Written to be read, not to hide.",
    sections: [
      {
        h: "What we collect",
        body: [
          "If you create anonymously, your work lives on your device and we collect nothing that identifies you. If you sign in, we store your email, a hashed password, and the music and objects you choose to sync.",
          "Analytics are off until you consent. If you turn them on, we collect anonymous product-usage events through our analytics providers to understand what to improve. We never sell your data.",
        ],
      },
      {
        h: "Why we collect it",
        body: [
          "To keep your universe safe across devices, to let you collaborate and publish, and, only with your consent, to learn which features help creators most.",
        ],
      },
      {
        h: "Your controls",
        body: ["You are in charge."],
        list: [
          "Create and use Beat Draft without an account.",
          "Turn analytics on or off at any time in Cookie preferences.",
          "Export your work as an open package whenever you want.",
          "Ask us to delete your account and cloud data at hello@beatdraft.com.",
        ],
      },
      {
        h: "Data location and retention",
        body: [
          "Cloud data is stored on Cloudflare infrastructure. We keep it while your account is active and remove it on request. Local data lives in your browser until you clear it.",
        ],
      },
    ],
  },
  {
    slug: "terms",
    kind: "legal",
    title: "Terms of Service",
    updated: UPDATED,
    intro: "The agreement between you and Beat Draft. Plain language, no traps.",
    sections: [
      {
        h: "Using Beat Draft",
        body: [
          "You may use Beat Draft to make, publish, perform and share music. You are responsible for what you make and publish, and for following the Acceptable Use Policy and Community Guidelines.",
        ],
      },
      {
        h: "Your content",
        body: [
          "You keep ownership of your music. By publishing a Session, you grant other creators the rights described in its license and grant us the limited rights needed to host, display and stream it on the platform.",
        ],
      },
      {
        h: "Availability",
        body: [
          "We work hard to keep Beat Draft running and your work safe, but the service is provided as is. We are not liable for lost work beyond what the law requires, which is why your universe also lives on your device and exports openly.",
        ],
      },
      {
        h: "Changes and ending",
        body: [
          "We may update these terms; material changes will be announced. You can stop using Beat Draft at any time and take your work with you through export.",
        ],
      },
    ],
  },
  {
    slug: "cookies",
    kind: "legal",
    title: "Cookie Policy",
    updated: UPDATED,
    intro: "What we store in your browser and why. Most of it is just so the product works.",
    sections: [
      {
        h: "Strictly necessary",
        body: [
          "We store your theme, interface preferences and your local universe on your device. When you sign in, a secure session cookie keeps you signed in. None of this is used for tracking, and it cannot be turned off without breaking the product.",
        ],
      },
      {
        h: "Analytics, only with consent",
        body: [
          "If you consent, our analytics providers set cookies to measure anonymous product usage. If you do not consent, these are never set.",
          "Manage this any time using Cookie preferences below or in Settings.",
        ],
      },
    ],
  },
  {
    slug: "acceptable-use",
    kind: "legal",
    title: "Acceptable Use Policy",
    updated: UPDATED,
    intro: "A short list of things that keep Beat Draft a good place to create.",
    sections: [
      {
        h: "Do not",
        body: ["Using Beat Draft to do any of the following is not allowed."],
        list: [
          "Upload or publish music you do not have the rights to.",
          "Harass, threaten or impersonate other creators.",
          "Attack, overload or attempt to break the service.",
          "Use the platform for illegal activity or to distribute malware.",
        ],
      },
      {
        h: "Enforcement",
        body: ["We may remove content or suspend accounts that break this policy. Where we can, we warn first and explain why."],
      },
    ],
  },
  {
    slug: "community-guidelines",
    kind: "legal",
    title: "Community Guidelines",
    updated: UPDATED,
    intro: "Beat Draft is creators inspiring creators. These keep it that way.",
    sections: [
      {
        h: "Be generous",
        body: [
          "Fork freely, credit always. When you build on someone's work, their name travels with it. That is not a rule to endure, it is the whole point.",
        ],
      },
      {
        h: "Be kind",
        body: [
          "Feedback is welcome; cruelty is not. Assume the person behind a song is learning, same as you.",
        ],
      },
      {
        h: "Be honest",
        body: ["Do not pass off others' work as your own. Provenance is public here for a reason."],
      },
    ],
  },
  {
    slug: "dmca",
    kind: "legal",
    title: "Copyright and DMCA",
    updated: UPDATED,
    intro: "How to report infringement and how we respond.",
    sections: [
      {
        h: "Reporting",
        body: [
          "If you believe something on Beat Draft infringes your copyright, email dmca@beatdraft.com with the work concerned, the material you are reporting, your contact details, and a good-faith statement.",
        ],
      },
      {
        h: "Our response",
        body: [
          "We review reports promptly and remove or restrict infringing material where warranted. Repeat infringers lose access. If your work was removed by mistake, you can appeal to the same address.",
        ],
      },
    ],
  },
  {
    slug: "licensing",
    kind: "legal",
    title: "Licensing Policy",
    updated: UPDATED,
    intro: "Licenses are how a living song tells others what they may build on. Think open-source licenses, for music.",
    sections: [
      {
        h: "The license choices",
        body: ["When you release a Session, you choose how others may use its ideas."],
        list: [
          "Remix ok: others may fork and build on it, with credit.",
          "Credit required: use is welcome, attribution travels with the work.",
          "Commercial needs permission: ask the creator before commercial use.",
        ],
      },
      {
        h: "Credit chains",
        body: [
          "Every fork, sample and installed Pack composes a machine-readable credit chain from the provenance graph. Names travel with objects into other people's music, automatically.",
        ],
      },
    ],
  },
  {
    slug: "ai-usage",
    kind: "legal",
    title: "AI Usage Policy",
    updated: UPDATED,
    intro: "How Rhodes works, what it does with your music, and the promises around it.",
    sections: [
      {
        h: "How Rhodes works",
        body: [
          "Beat Draft's music engines always work, offline and without any model. On top of them, an AI layer can enrich suggestions when configured. If it is unavailable or slow, we fall back silently to the engines so you are never blocked.",
        ],
      },
      {
        h: "Your music and models",
        body: [
          "We do not use your private music to train third-party models. Context is sent to the AI layer only to fulfil a request you made, and only when you have an AI-enabled setup.",
        ],
      },
      {
        h: "Taste and memory",
        body: [
          "Rhodes learns your taste from what you accept and reject. This memory is yours, append-only, and never wiped by a model upgrade. Models change; your memory is forever.",
        ],
      },
    ],
  },
  {
    slug: "marketplace-terms",
    kind: "legal",
    title: "Marketplace Terms",
    updated: UPDATED,
    intro: "The rules for buying and installing Packs.",
    sections: [
      {
        h: "Installing",
        body: [
          "Installing a Pack brings its objects into your universe with provenance. Free Packs install immediately. Where payments are enabled and a Pack is priced, checkout happens before install.",
        ],
      },
      {
        h: "Rights",
        body: [
          "A Pack's license tells you what you may do with it. You may use installed sounds in your own music within those terms. You may not resell a Pack's contents as your own.",
        ],
      },
    ],
  },
  {
    slug: "creator-terms",
    kind: "legal",
    title: "Creator Terms",
    updated: UPDATED,
    intro: "For creators who publish Sessions, Packs, personalities and extensions.",
    sections: [
      {
        h: "What you publish",
        body: [
          "You confirm you have the rights to everything you publish, and you set its license. You keep ownership; you grant the platform the rights needed to host and display it, and other creators the rights your license describes.",
        ],
      },
      {
        h: "Payments",
        body: [
          "Selling is opt-in and, until payments are configured, everything is free. When payments are enabled, terms for payout, fees and refunds will be presented before you list anything priced.",
        ],
      },
      {
        h: "Extensions",
        body: [
          "Extensions run sandboxed and speak only the versioned bridge. They must declare their capabilities honestly and may not attempt to exceed them.",
        ],
      },
    ],
  },
];

export function companyPageBySlug(slug: string): CompanyPage | undefined {
  return COMPANY_PAGES.find((p) => p.slug === slug);
}

export const LEGAL_PAGES = COMPANY_PAGES.filter((p) => p.kind === "legal");
export const COMPANY_ONLY_PAGES = COMPANY_PAGES.filter((p) => p.kind === "company");
